import React, { useContext, useEffect, useState } from 'react';
import { AuthContext } from '../../../contexts/user-context/user-context';
import axios from 'axios';
import { API_ENDPOINT } from '../../../configurations/global.config';
import { add, displayMoney, minus, Money } from '../../../utils/money';
import { timestampedMessage } from '../../common/multi-package-scanner/multi-package-scanner';
import SnackBar from '../../common/snack-bar/snack-bar';
import LocalAtmIcon from '@mui/icons-material/LocalAtm';
import {
  CircularProgress,
  Card,
  CardContent,
  Typography,
  Grid,
  Divider,
  Paper,
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  ExpandMore,
  MonetizationOn,
  AttachMoney,
  RemoveCircleOutline,
} from '@mui/icons-material';
import { IconWithText } from '../../common/styles/components';

export interface PendingCash {
  total_price: Money[];
  total_delivery_fee: Money[];
  total_order_commission: Money[];
  total_pickup_commission: Money[];
  total_pop: Money[];
}

function PendingCashOperation() {
  const { user, config } = useContext(AuthContext);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [totalCash, setTotalCash] = useState<PendingCash | null>(null);
  const [snackBar, setSnackBar] = useState<timestampedMessage | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const response = await axios.get<PendingCash>(
          `${API_ENDPOINT}/api/v1/cash/`,
          config
        );
        setTotalCash(response.data);
      } catch (e) {
        setSnackBar({
          content: 'Failed to get cash',
          level: 'error',
          timestamp: Date.now(),
        });
      }
      setIsLoading(false);
    };
    fetchData();
  }, []);

  return (
    <Accordion sx={{ maxWidth: 600, mx: 'auto', mt: 3, borderRadius: 2 }}>
      <AccordionSummary
        expandIcon={<ExpandMore />}
        style={{ textAlign: 'center' }}
      >
        <Typography variant="h5" fontWeight="bold" align="center">
          <IconWithText centered>
            {' '}
            <LocalAtmIcon color="primary" fontSize="large" /> Active Cash
          </IconWithText>
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Paper elevation={0} sx={{ borderRadius: 2 }}>
          {isLoading || !totalCash ? (
            <Grid container justifyContent="center" alignItems="center">
              <CircularProgress size={24} />
              <Typography sx={{ ml: 1 }}>Loading cash details...</Typography>
            </Grid>
          ) : (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Card
                  sx={{
                    p: 2,
                    display: 'flex',
                    alignItems: 'center',
                    boxShadow: 1,
                  }}
                >
                  <MonetizationOn
                    color="success"
                    sx={{ fontSize: 32, mr: 2 }}
                  />
                  <Box>
                    <Typography variant="body1" fontWeight="bold">
                      Total COD
                    </Typography>
                    <Typography variant="h6" color="primary">
                      {displayMoney(
                        add(totalCash.total_price, totalCash.total_delivery_fee)
                      )}
                    </Typography>
                  </Box>
                </Card>
              </Grid>
              <Grid item xs={12}>
                <Card
                  sx={{
                    p: 2,
                    display: 'flex',
                    alignItems: 'center',
                    boxShadow: 1,
                  }}
                >
                  <AttachMoney color="warning" sx={{ fontSize: 32, mr: 2 }} />
                  <Box>
                    <Typography variant="body1" fontWeight="bold">
                      Total Orders Commission
                    </Typography>
                    <Typography variant="h6">
                      {displayMoney(totalCash.total_order_commission)}
                    </Typography>
                  </Box>
                </Card>
              </Grid>
              <Grid item xs={12}>
                <Card
                  sx={{
                    p: 2,
                    display: 'flex',
                    alignItems: 'center',
                    boxShadow: 1,
                  }}
                >
                  <AttachMoney color="info" sx={{ fontSize: 32, mr: 2 }} />
                  <Box>
                    <Typography variant="body1" fontWeight="bold">
                      Total Pickup Commission
                    </Typography>
                    <Typography variant="h6">
                      {displayMoney(totalCash.total_pickup_commission)}
                    </Typography>
                  </Box>
                </Card>
              </Grid>
              <Grid item xs={12}>
                <Card
                  sx={{
                    p: 2,
                    display: 'flex',
                    alignItems: 'center',
                    boxShadow: 1,
                  }}
                >
                  <AttachMoney color="info" sx={{ fontSize: 32, mr: 2 }} />
                  <Box>
                    <Typography variant="body1" fontWeight="bold">
                      Total Pay on Pickup
                    </Typography>
                    <Typography variant="h6">
                      {displayMoney(totalCash.total_pop)}
                    </Typography>
                  </Box>
                </Card>
              </Grid>
              <Grid item xs={12}>
                <Card
                  sx={{
                    p: 2,
                    display: 'flex',
                    alignItems: 'center',
                    bgcolor: '#f3f4f6',
                    boxShadow: 1,
                  }}
                >
                  <RemoveCircleOutline
                    color="primary"
                    sx={{ fontSize: 32, mr: 2 }}
                  />
                  <Box>
                    <Typography variant="body1" fontWeight="bold">
                      Net Due
                    </Typography>
                    <Typography variant="h6">
                      {displayMoney(
                        minus(
                          minus(
                            add(
                              totalCash.total_price,
                              totalCash.total_delivery_fee
                            ),
                            totalCash.total_pop
                          ),
                          add(
                            totalCash.total_order_commission,
                            totalCash.total_pickup_commission
                          )
                        )
                      )}
                    </Typography>
                  </Box>
                </Card>
              </Grid>
            </Grid>
          )}
          {snackBar && snackBar.content && (
            <SnackBar
              message={snackBar.content}
              level={snackBar.level}
              key={snackBar.timestamp}
            />
          )}
        </Paper>
      </AccordionDetails>
    </Accordion>
  );
}

export default PendingCashOperation;
